/**
 * AssistantPanel — «AS Ассистент»: чат с ИИ, открывается кнопкой в TopBar (видна только при
 * GET /assistant/access → enabled). Справа на десктопе, во весь экран на телефоне.
 *
 * Контракт — as-life-api/docs/ai-assistant.md. История и аргументы инструментов живут на сервере;
 * панель лишь ведёт шаги беседы:
 *   message → (инструменты чтения выполняются сами: run_tool) → continue → … → ответ;
 *   инструмент записи (согласовать/отклонить/вернуть) → run_tool даёт предпросмотр → карточка
 *   «Подтвердить / Отмена» → confirm_tool → continue.
 * Ссылки в ответах кликабельны (портал — внутри приложения, прочее — во внешнем окне).
 * Ошибки — человеческим текстом: лимит на сегодня, нет доступа, сервер недоступен.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Linking, Platform } from 'react-native'
import { useRouter } from 'expo-router'
import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, Input, ScrollView, Stack, Text, XStack, YStack } from 'tamagui'

import { api, ApiError } from '../api/client'
import { useApp } from '../i18n'
import { useResponsive } from '../lib/responsive'
import type { AssistantItem, AssistantStepBody, AssistantStepResponse } from '../lib/types'
import { useMe } from '../features/users/useUsers'
import { useAssistantConversations } from '../features/assistant/useAssistant'
import { Button, Icon, IconButton } from '../ui'
import { palette } from '../theme/palette'
import { tone } from '../theme/tones'

const isWeb = Platform.OS === 'web'
/** Страховка от бесконечного цикла шагов (модель + инструменты) на одно сообщение */
const MAX_STEPS = 12

/** Ссылка Markdown [текст](адрес) или голый адрес — split по нему даёт куски текста и ссылок. */
const LINK_RE = /(\[[^\]\n]+\]\(https?:\/\/[^\s)]+\)|https?:\/\/[^\s)\]]+)/g
const MD_LINK = /^\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)$/
const isUrl = (s: string) => /^https?:\/\//.test(s)

/**
 * Модель просят писать без Markdown, но страхуемся: таблица → строки «a · b», без заголовков,
 * звёздочек и обратных кавычек; маркеры списка → «— ».
 */
export function toPlain(md: string): string {
  return md
    .split('\n')
    .filter((l) => !(l.includes('--') && /^[\s|:-]+$/.test(l)))
    .map((l) =>
      /^\s*\|.*\|\s*$/.test(l)
        ? l.trim().slice(1, -1).split('|').map((c) => c.trim()).filter(Boolean).join(' · ')
        : l,
    )
    .map((l) => l.replace(/^#{1,6}\s+/, '').replace(/^(\s*)[-*]\s+/, '$1— '))
    .join('\n')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/`([^`\n]+)`/g, '$1')
}
/** Инструменты хаба, у которых есть подпись «что делаю» (ai.tool.<name>); прочие — ai.tool.default */
const TOOL_LABELS = new Set(['whoami', 'my_vacation_balance', 'my_approvals', 'my_requests', 'get_request', 'absences', 'find_person', 'kb_search', 'upcoming_events', 'decide_task'])

/** Текст ответа: простым текстом (см. toPlain), ссылки — кликабельные. */
function RichText({ text, color, onLink }: { text: string; color: string; onLink: (url: string) => void }) {
  const parts = toPlain(text).split(LINK_RE)
  const link = (key: number, url: string, label: string) => (
    <Text key={key} color="$primary" textDecorationLine="underline" cursor="pointer" onPress={() => onLink(url)}>
      {label}
    </Text>
  )
  return (
    <Text fontSize={13} lineHeight={20} color={color}>
      {parts.map((p, i) => {
        const md = MD_LINK.exec(p)
        if (md) return link(i, md[2], md[1])
        if (isUrl(p)) return link(i, p, p.replace(/^https?:\/\/[^/]+/, '') || p)
        return <Text key={i}>{p}</Text>
      })}
    </Text>
  )
}

function Bubble({ me, children }: { me: boolean; children: ReactNode }) {
  return (
    <YStack alignItems={me ? 'flex-end' : 'flex-start'}>
      <Stack
        maxWidth="88%"
        paddingHorizontal={13}
        paddingVertical={10}
        borderRadius={12}
        borderBottomRightRadius={me ? 3 : 12}
        borderBottomLeftRadius={me ? 12 : 3}
        backgroundColor={me ? '$primary' : '$bgLayout'}
      >
        {children}
      </Stack>
    </YStack>
  )
}

export function AssistantPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { l } = useApp()
  const { isPhone } = useResponsive()
  const router = useRouter()
  const qc = useQueryClient()
  const { data: me } = useMe()

  const [convId, setConvId] = useState<string | null>(null)
  const [items, setItems] = useState<AssistantItem[]>([])
  const [val, setVal] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const scrollRef = useRef<{ scrollToEnd: (opts?: { animated?: boolean }) => void } | null>(null)
  const history = useAssistantConversations(open && showHistory)

  const firstName = me?.fullName?.split(' ')[0] ?? ''
  const suggestions = [l('ai.s1'), l('ai.s2'), l('ai.s3')]

  useEffect(() => {
    const id = setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 50)
    return () => clearTimeout(id)
  }, [items, busy])

  // Esc закрывает панель (web).
  useEffect(() => {
    if (!open || !isWeb) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const errorText = (err: unknown): string => {
    if (err instanceof ApiError) {
      if (err.status === 429) return l('ai.limit')
      if (err.status === 403) return l('ai.noAccess')
    }
    return l('ai.error')
  }

  const openLink = (url: string) => {
    if (isWeb && url.startsWith(window.location.origin)) router.push(url.slice(window.location.origin.length) as never)
    else void Linking.openURL(url)
    if (isPhone) onClose()
  }

  /** Ведёт беседу шагами, пока модель не ответит или не понадобится подтверждение. */
  const drive = async (id: string, first: AssistantStepBody) => {
    let res: AssistantStepResponse = await api.assistantStep(id, first)
    setItems(res.conversation.items)
    for (let i = 0; i < MAX_STEPS; i++) {
      const read = res.pendingToolUses.find((p) => p.kind === 'read')
      const write = res.pendingToolUses.find((p) => p.kind === 'write')
      if (read) res = await api.assistantStep(id, { action: 'run_tool', toolUseId: read.id })
      else if (write) {
        const hasCard = res.conversation.items.some((it) => 'kind' in it && it.kind === 'confirm' && it.toolUseId === write.id)
        if (hasCard) return // ждём «Подтвердить / Отмена»
        res = await api.assistantStep(id, { action: 'run_tool', toolUseId: write.id })
      } else if (!res.done) res = await api.assistantStep(id, { action: 'continue' })
      else return
      setItems(res.conversation.items)
    }
  }

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
      qc.invalidateQueries({ queryKey: ['assistant-access'] })
    }
  }

  const send = (text?: string) => {
    const q = (text ?? val).trim()
    if (!q || busy) return
    setVal('')
    setShowHistory(false)
    setItems((cur) => [...cur, { role: 'user', text: q }]) // сразу видно, сервер вернёт то же самое
    void run(async () => {
      let id = convId
      if (!id) {
        id = (await api.createAssistantConversation()).id
        setConvId(id)
      }
      await drive(id, { action: 'message', text: q })
      qc.invalidateQueries({ queryKey: ['assistant-conversations'] })
    })
  }

  const confirm = (toolUseId: string, approve: boolean) => {
    if (!convId || busy) return
    void run(() => drive(convId, { action: 'confirm_tool', toolUseId, approve }))
  }

  const newChat = () => {
    setConvId(null)
    setItems([])
    setError(null)
    setShowHistory(false)
  }

  const openConversation = (id: string) => {
    setShowHistory(false)
    void run(async () => {
      const c = await api.assistantConversation(id)
      setConvId(c.id)
      setItems(c.items)
    })
  }

  const renderItem = (it: AssistantItem, i: number) => {
    if ('role' in it) {
      const mine = it.role === 'user'
      return (
        <Bubble key={i} me={mine}>
          {mine ? (
            <Text fontSize={13} lineHeight={20} color="$onPrimary">
              {it.text}
            </Text>
          ) : (
            <RichText text={it.text} color="$color" onLink={openLink} />
          )}
        </Bubble>
      )
    }
    if (it.kind === 'tool') {
      return (
        <XStack key={i} alignItems="center" gap={6} paddingHorizontal={4}>
          <Icon name={it.status === 'error' ? 'triangle-alert' : 'check'} size={12} color="$tertiary" />
          <Text fontSize={11} color="$tertiary">
            {l(TOOL_LABELS.has(it.name) ? `ai.tool.${it.name}` : 'ai.tool.default')}
          </Text>
        </XStack>
      )
    }
    // карточка подтверждения действия
    return (
      <YStack key={i} borderWidth={1} borderColor={it.status === 'pending' ? '$primary' : '$borderSecondary'} borderRadius={10} padding={12} gap={8} backgroundColor="$background">
        <XStack alignItems="center" gap={8}>
          <Icon name="circle-check-big" size={16} color="$primary" />
          <Text flex={1} fontSize={13} fontWeight="700" color="$color">
            {it.title}
          </Text>
        </XStack>
        {it.preview ? (
          <Text fontSize={12} lineHeight={18} color="$muted">
            {it.preview}
          </Text>
        ) : null}
        {it.status === 'pending' ? (
          <XStack gap={8}>
            <Button variant="primary" flex={1} disabled={busy} onPress={() => confirm(it.toolUseId, true)}>
              {l('ai.confirm')}
            </Button>
            <Button flex={1} disabled={busy} onPress={() => confirm(it.toolUseId, false)}>
              {l('ai.cancel')}
            </Button>
          </XStack>
        ) : (
          <Text fontSize={12} fontWeight="700" color={it.status === 'done' ? '$tone_green' : '$muted'}>
            {l(it.status === 'done' ? 'ai.done' : 'ai.cancelled')}
          </Text>
        )}
      </YStack>
    )
  }

  return (
    <AnimatePresence>
      {open && (
        <YStack key="assistant" position={isWeb ? ('fixed' as 'absolute') : 'absolute'} top={0} left={0} right={0} bottom={0} zIndex={200}>
          {/* backdrop */}
          <Stack
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            backgroundColor={palette.scrim}
            onPress={onClose}
            animation="quicker"
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
            opacity={1}
          />

          {/* panel — pinned right, slides in/out */}
          <YStack
            animation="quicker"
            enterStyle={{ x: 440, opacity: 0 }}
            exitStyle={{ x: 440, opacity: 0 }}
            x={0}
            opacity={1}
            position="absolute"
            top={0}
            right={0}
            bottom={0}
            width={isPhone ? '100%' : 420}
            maxWidth="100%"
            backgroundColor="$background"
            borderLeftWidth={1}
            borderColor="$borderSecondary"
            shadowColor={palette.shadow}
            shadowOpacity={0.22}
            shadowRadius={28}
            shadowOffset={{ width: -8, height: 0 }}
          >
            {/* header */}
            <XStack alignItems="center" gap={8} paddingHorizontal={16} height={64} borderBottomWidth={1} borderColor="$borderSecondary">
              <Stack width={30} height={30} borderRadius={999} backgroundColor="$primary" alignItems="center" justifyContent="center">
                <Icon name="sparkles" size={16} color="$onPrimary" />
              </Stack>
              <Text flex={1} fontSize={14} fontWeight="700" color="$color">
                {l('ai.title')}
              </Text>
              <IconButton onPress={() => setShowHistory((v) => !v)} aria-label={l('ai.history')}>
                <Icon name="list" size={17} color={showHistory ? '$primary' : '$muted'} />
              </IconButton>
              <IconButton onPress={newChat} aria-label={l('ai.newChat')}>
                <Icon name="plus" size={18} color="$muted" />
              </IconButton>
              <IconButton onPress={onClose} aria-label={l('ai.close')}>
                <Icon name="x" size={18} color="$muted" />
              </IconButton>
            </XStack>

            {showHistory ? (
              <ScrollView flex={1} contentContainerStyle={{ padding: 12, gap: 4 }}>
                {history.isLoading ? (
                  <Text fontSize={13} color="$muted" padding={8}>
                    …
                  </Text>
                ) : (history.data ?? []).length === 0 ? (
                  <Text fontSize={13} color="$muted" padding={8}>
                    {l('ai.noHistory')}
                  </Text>
                ) : (
                  (history.data ?? []).map((c) => (
                    <XStack
                      key={c.id}
                      onPress={() => openConversation(c.id)}
                      cursor="pointer"
                      padding={10}
                      borderRadius={7}
                      gap={10}
                      alignItems="center"
                      hoverStyle={{ backgroundColor: '$backgroundHover' }}
                      backgroundColor={c.id === convId ? '$primaryBg' : undefined}
                    >
                      <Icon name="message-circle" size={15} color="$tertiary" />
                      <Text flex={1} fontSize={13} color="$color" numberOfLines={1}>
                        {c.title}
                      </Text>
                      <Text fontSize={11} color="$tertiary">
                        {new Date(c.updatedAt).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}
                      </Text>
                    </XStack>
                  ))
                )}
              </ScrollView>
            ) : (
              <ScrollView ref={scrollRef as never} flex={1} contentContainerStyle={{ padding: 18, gap: 12 }} keyboardShouldPersistTaps="handled">
                <Bubble me={false}>
                  <Text fontSize={13} lineHeight={20} color="$color">
                    {firstName ? l('ai.welcome').replace('{name}', firstName) : l('ai.welcomeAnon')}
                  </Text>
                </Bubble>
                {items.map(renderItem)}
                {busy ? (
                  <XStack alignItems="center" gap={6} paddingHorizontal={4}>
                    <Icon name="sparkles" size={12} color="$primary" />
                    <Text fontSize={12} color="$muted">
                      {l('ai.thinking')}
                    </Text>
                  </XStack>
                ) : null}
                {error ? (
                  <XStack alignItems="flex-start" gap={6} padding={10} borderRadius={7} backgroundColor={tone('red').soft}>
                    <Icon name="triangle-alert" size={14} color={tone('red').text} />
                    <Text flex={1} fontSize={12} color="$color">
                      {error}
                    </Text>
                  </XStack>
                ) : null}
              </ScrollView>
            )}

            {/* подсказки — только в пустой беседе */}
            {!showHistory && items.length === 0 ? (
              <XStack flexWrap="wrap" gap={8} paddingHorizontal={18} paddingBottom={12}>
                {suggestions.map((s, i) => (
                  <Stack
                    key={i}
                    onPress={() => send(s)}
                    cursor="pointer"
                    paddingHorizontal={11}
                    paddingVertical={6}
                    borderRadius={999}
                    borderWidth={1}
                    borderColor="$borderColor"
                    hoverStyle={{ backgroundColor: '$backgroundHover', borderColor: '$primary' }}
                  >
                    <Text fontSize={12} color="$muted">
                      {s}
                    </Text>
                  </Stack>
                ))}
              </XStack>
            ) : null}

            {/* input */}
            <XStack gap={8} paddingHorizontal={16} paddingVertical={12} borderTopWidth={1} borderColor="$borderSecondary">
              <Input
                flex={1}
                height={40}
                fontSize={14}
                borderRadius={7}
                borderColor="$borderColor"
                backgroundColor="$background"
                color="$color"
                placeholder={l('ai.placeholder')}
                placeholderTextColor="$tertiary"
                value={val}
                maxLength={4000}
                onChangeText={setVal}
                onSubmitEditing={() => send()}
                returnKeyType="send"
                editable={!busy}
                focusStyle={{ borderColor: '$primary' }}
              />
              <IconButton
                onPress={() => send()}
                disabled={busy}
                opacity={busy ? 0.5 : 1}
                backgroundColor="$primary"
                hoverStyle={{ backgroundColor: '$primaryActive' }}
                aria-label={l('ai.send')}
              >
                <Icon name="send" size={17} color="$onPrimary" />
              </IconButton>
            </XStack>
          </YStack>
        </YStack>
      )}
    </AnimatePresence>
  )
}
